import { useQuery } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "wouter";
import type { OrderWithItems } from "@shared/schema";
import { useCompanyLogo } from "@/hooks/use-company-logo";

export default function DeliveryNotePage() {
  const { id } = useParams<{ id: string }>();

  const logoUrl = useCompanyLogo();

  const { data: order, isLoading } = useQuery<OrderWithItems>({
    queryKey: ["/api/admin/orders", id],
  });

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Order hittades inte</h1>
        <Button asChild variant="outline">
          <Link href="/admin">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap no-print">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link href="/admin">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Leveranssedel</h1>
            <p className="text-muted-foreground">
              Order #{order.orderNumber}
            </p>
          </div>
        </div>
        <Button onClick={handlePrint} data-testid="button-print">
          <Printer className="h-4 w-4 mr-2" />
          Skriv ut
        </Button>
      </div>

      <div className="delivery-note-container a4-page mx-auto bg-background dark:bg-white text-foreground dark:text-black print:bg-white print:text-black shadow-lg print:shadow-none">
        <div className="p-8 space-y-6">
          <div className="flex justify-between items-start">
            <h1 className="text-4xl font-bold italic">Följesedel</h1>
            <img
              src={logoUrl}
              alt="Rätt Order"
              className="h-16 w-auto max-w-[120px] object-contain"
              data-testid="img-delivery-logo"
            />
          </div>

          <div className="h-1 bg-primary w-full" />

          <div className="grid grid-cols-2 gap-8 pt-4">
            <div>
              {settings?.companyName && (
                <h2 className="font-bold text-lg mb-2">{settings.companyName}</h2>
              )}
              {settings?.companyAddress && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{settings.companyAddress}</p>
              )}
              {settings?.companyPhone && (
                <p className="text-muted-foreground dark:text-gray-600 italic">Telefon: {settings.companyPhone}</p>
              )}
              {settings?.companyEmail && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{settings.companyEmail}</p>
              )}
            </div>
            <div>
              <h2 className="font-bold text-lg mb-2">Mottagare</h2>
              <p className="text-muted-foreground dark:text-gray-600 italic">{order.client?.name}</p>
              {order.client?.address && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.address}</p>
              )}
              {order.client?.phone && (
                <p className="text-muted-foreground dark:text-gray-600 italic">Tel: {order.client.phone}</p>
              )}
              {order.client?.email && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.email}</p>
              )}
            </div>
          </div>

          <div className="pt-8">
            <div className="h-px bg-primary w-full mb-4" />
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th className="py-2 font-semibold">Beskrivning</th>
                  <th className="py-2 font-semibold">Datum</th>
                  <th className="py-2 font-semibold text-right">Antal</th>
                  <th className="py-2 font-semibold text-right">Enhet</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item) => (
                  <tr key={item.id} className="border-t border-border dark:border-gray-200">
                    <td className="py-3">{item.product?.name}</td>
                    <td className="py-3 text-muted-foreground dark:text-gray-600">
                      {format(new Date(order.orderDate!), "yyyy-MM-dd", { locale: sv })}
                    </td>
                    <td className="py-3 text-right">{item.quantity}</td>
                    <td className="py-3 text-right">{getUnitLabel(item.product?.sellUnit || "pcs")}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border dark:border-gray-300 font-semibold">
                  <td className="py-3">Totalt</td>
                  <td></td>
                  <td className="py-3 text-right">{order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {(order.client?.hasRut && order.client.rutDetails) || order.client?.notes ? (
            <div className="pt-4 grid grid-cols-2 gap-8">
              <div>
                {order.client?.hasRut && order.client.rutDetails && (
                  <>
                    <h3 className="font-semibold mb-2">Leveransrutt</h3>
                    <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.rutDetails}</p>
                  </>
                )}
              </div>
              <div>
                {order.client?.notes && (
                  <>
                    <h3 className="font-semibold mb-2">Anteckningar</h3>
                    <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.notes}</p>
                  </>
                )}
              </div>
            </div>
          ) : null}

        </div>
      </div>

      <style>{`
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
        
        @media screen and (max-width: 850px) {
          .a4-page {
            width: 100%;
            min-height: auto;
          }
        }
        
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          body * {
            visibility: hidden;
          }
          .delivery-note-container,
          .delivery-note-container * {
            visibility: visible;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .delivery-note-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            margin: 0;
            padding: 10mm;
            box-sizing: border-box;
            background: white !important;
            color: black !important;
            overflow: visible !important;
            page-break-after: avoid;
            page-break-inside: avoid;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
